import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  Search,
  
  Close
} from "@mui/icons-material";
import filterIcon from "../../assets/filterIcon.png";
import CustomInputField from "../common/CustomInputField";
import { subscriptionService } from "../../services/subscriptionService";
import { getPageNumbers } from "../../utils/pagination";

const Subscription = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [addSubscriptionOpen, setAddSubscriptionOpen] = useState(false);

  // Form state for Add Subscription Modal
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscriptionName: "",
    subscriptionDuration: "",
    coins: "",
    amount: ""
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  // Subscriptions data fetched from API
  const [subscriptionsData, setSubscriptionsData] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const result = await subscriptionService.getPlans();
        if (!mounted) return;
        if (result.success) {
          const raw = Array.isArray(result.data) ? result.data : (result.data?.result || []);
          const transformed = raw.map((p) => ({
            id: p._id?.$oid || p._id || '',
            subscriptionName: p.name || 'N/A',
            duration: p.duration_days ? `${p.duration_days} days` : (p.type === 'subscription' ? 'Recurring' : 'One-time'),
            coins: p.credits != null ? `${p.credits} Coins` : '0 Coins',
            credits: `${p.currency || ''} ${p.price != null ? p.price : ''}`.trim(),
            raw: p
          }));
          setSubscriptionsData(transformed);
        } else {
          setPlansError(result.message || 'Failed to fetch plans');
        }
      } catch (err) {
        console.error('Error loading plans:', err);
        if (!mounted) return;
        setPlansError('Failed to fetch plans. Please try again.');
      } finally {
        if (mounted) setPlansLoading(false);
      }
    };

    loadPlans();

    return () => { mounted = false; };
  }, []);

  const itemsPerPage = 10;
  // Filter subscriptions based on search term (guard missing fields)
  const filteredSubscriptions = subscriptionsData.filter(subscription => {
    const name = (subscription?.subscriptionName || subscription?.name || "").toString().toLowerCase();
    const duration = (subscription?.duration || "").toString().toLowerCase();
    const coins = (subscription?.coins || "").toString().toLowerCase();
    const q = (searchTerm || "").toLowerCase();

    return (
      name.includes(q) ||
      duration.includes(q) ||
      coins.includes(q)
    );
  });

  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSubscriptions.slice(startIndex, endIndex);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // const handleAddSubscriptionOpen = () => {
  //   setAddSubscriptionOpen(true);
  // };

  const handleAddSubscriptionClose = () => {
    setAddSubscriptionOpen(false);
    setSubscriptionForm({
      subscriptionName: "",
      subscriptionDuration: "",
      coins: "",
      amount: ""
    });
    setFieldErrors({});
  };

  const handleFormChange = (field, value) => {
    setSubscriptionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomize = () => {
    setFieldErrors({});
    const errors = {};

    if (!subscriptionForm.subscriptionName.trim()) {
      errors.subscriptionName = "Subscription name is required";
    }

    if (!subscriptionForm.subscriptionDuration.trim()) {
      errors.subscriptionDuration = "Subscription duration is required";
    }

    if (!subscriptionForm.coins.trim()) {
      errors.coins = "Coins value is required";
    }

    if (!subscriptionForm.amount.trim()) {
      errors.amount = "Amount is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Call API to create subscription plan
    (async () => {
      setActionLoading(true);
      try {
        const name = subscriptionForm.subscriptionName.trim();
        const credits = Number(subscriptionForm.coins) || 0;
        const price = Number(subscriptionForm.amount) || 0;
        const type = 'pack';

        const result = await subscriptionService.addPlan(name, credits, price, type);

        if (result.success) {
          alert(result.message || 'Subscription created successfully');
          handleAddSubscriptionClose();
        } else {
          alert(result.message || 'Failed to create subscription');
        }
      } catch (err) {
        console.error('Error creating subscription plan:', err);
        alert('Failed to create subscription. Please try again.');
      } finally {
        setActionLoading(false);
      }
    })();
  };

  const handleReset = () => {
    setSubscriptionForm({
      subscriptionName: "",
      subscriptionDuration: "",
      coins: "",
      amount: ""
    });
    setFieldErrors({});
  };

  const isFormChanged = () => {
    return Object.values(subscriptionForm).some((v) => (v || "").toString().trim() !== "");
  };

  return (
    <Box sx={{ padding: "24px", backgroundColor: "#000000", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "32px"
      }}>
        <Box>
          <Typography sx={{
            color: "#FFFFFF",
            fontSize: "28px",
            fontWeight: 600,
            marginBottom: "8px"
          }}>
            Manage Subscriptions
          </Typography>
          <Typography sx={{
            color: "#888888",
            fontSize: "14px"
          }}>
            Create and manage subscription plans, coins, and credits.
          </Typography>
        </Box>

        {/* <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSubscriptionOpen}
          sx={{
            backgroundColor: "#E1AD01",
            color: "#000000",
            fontWeight: 600,
            padding: "12px 24px",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#E6B12A",
            },
          }}
        >
          Add Subscription
        </Button> */}
      </Box>

      {/* Search Section */}
      <Box sx={{ marginBottom: "32px" }}>
        <TextField
          placeholder="Search Subscription"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#888888" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "400px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "25px",
              height: "48px",
              "& fieldset": {
                border: "1px solid #E0E0E0",
              },
              "&:hover fieldset": {
                border: "1px solid #E1AD01",
              },
              "&.Mui-focused fieldset": {
                border: "2px solid #E1AD01",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "14px 16px",
              fontSize: "14px",
              color: "#333333",
              "&::placeholder": {
                color: "#666666",
                opacity: 1
              }
            },
          }}
        />
        <IconButton sx={{
          marginLeft: "16px",
          backgroundColor: "#222222",
          color: "#888888",
          "&:hover": {
            backgroundColor: "#333333",
          }
        }}>
          <Box component="img" src={filterIcon} alt="filter" sx={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>

      {/* Transactions Details Section */}
      <Box sx={{
        backgroundColor: "#111111",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "24px"
      }}>
        <Typography sx={{
          color: "#FFFFFF",
          fontSize: "20px",
          fontWeight: 600,
          marginBottom: "24px"
        }}>
          Transactions Details
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            boxShadow: "none"
          }}
        >
          {plansLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
              <CircularProgress sx={{ color: '#E1AD01' }} />
            </Box>
          ) : plansError ? (
            <Alert severity="error" sx={{ margin: 2 }}>{plansError}</Alert>
          ) : (
            <Table>
              <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#333333",
                  padding: "16px"
                }}>
                  Subscription ID
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#333333",
                  padding: "16px"
                }}>
                  Subscription Name
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#333333",
                  padding: "16px"
                }}>
                  Duration
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#333333",
                  padding: "16px"
                }}>
                  Coins
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#333333",
                  padding: "16px"
                }}>
                  Credits
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getCurrentPageItems().map((subscription, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "#FAFAFA",
                    },
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                >
                  <TableCell sx={{
                    padding: "16px",
                    fontSize: "14px",
                    color: "#333333"
                  }}>
                    {subscription.id}
                  </TableCell>
                  <TableCell sx={{
                    padding: "16px",
                    fontSize: "14px",
                    color: "#333333"
                  }}>
                    {subscription.subscriptionName}
                  </TableCell>
                  <TableCell sx={{
                    padding: "16px",
                    fontSize: "14px",
                    color: "#333333"
                  }}>
                    {subscription.duration}
                  </TableCell>
                  <TableCell sx={{
                    padding: "16px",
                    fontSize: "14px",
                    color: "#333333"
                  }}>
                    {subscription.coins}
                  </TableCell>
                  <TableCell sx={{
                    padding: "16px",
                    fontSize: "14px",
                    color: "#333333",
                    fontWeight: 600
                  }}>
                    {subscription.credits}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </TableContainer>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            sx={{
              borderColor: "#666666",
              color: "#CCCCCC",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#E1AD01",
                backgroundColor: "rgba(212, 175, 55, 0.1)",
              },
              "&:disabled": {
                borderColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {getPageNumbers(currentPage, totalPages).map((page, idx) =>
              page === "..." ? (
                <Box key={`ellipsis-${idx}`} sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}>...</Box>
              ) : (
                <Box
                  key={page}
                  onClick={() => handlePageChange(page)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor:
                      page === currentPage ? "#E1AD01" : "#333333",
                    color: page === currentPage ? "#000000" : "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        page === currentPage ? "#B8941F" : "#444444",
                    },
                  }}
                >
                  {page}
                </Box>
              )
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            sx={{
              backgroundColor: "#E1AD01",
              color: "#000000",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#B8941F",
              },
              "&:disabled": {
                backgroundColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Add Subscription Modal */}
      <Dialog
        open={addSubscriptionOpen}
        onClose={handleAddSubscriptionClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "0px",
            width: "65vw",
            maxWidth: "none",
            margin: "auto"
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "32px 40px 8px 40px",
          borderBottom: "none"
        }}>
          <Typography sx={{
            fontSize: "28px",
            fontWeight: 600,
            color: "#000000"
          }}>
            Add Subscription
          </Typography>
          <IconButton
            onClick={handleAddSubscriptionClose}
            sx={{
              color: "#999999",
              fontSize: "24px",
              "&:hover": {
                backgroundColor: "#F5F5F5",
                color: "#666666"
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "8px 40px 40px 40px" }}>
          <Typography sx={{
            color: "#666666",
            fontSize: "14px",
            marginBottom: "32px",
            fontWeight: 400
          }}>
            Manage Chat Integrations & costumizations
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* First Row - Subscription Name and Duration */}
            <Box sx={{ display: "flex", gap: "20px" }}>
              <Box sx={{ flex: 1 }}>
                <CustomInputField
                  label="Subscription Name"
                  placeholder="Enter Name"
                  value={subscriptionForm.subscriptionName}
                  onChange={(e) => {
                    handleFormChange("subscriptionName", e.target.value);
                    if (fieldErrors.subscriptionName) {
                      setFieldErrors(prev => ({ ...prev, subscriptionName: "" }));
                    }
                  }}
                  fullWidth
                />
                {fieldErrors.subscriptionName && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                    {fieldErrors.subscriptionName}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomInputField
                  label="Subscription Duration"
                  placeholder="Set Duration"
                  value={subscriptionForm.subscriptionDuration}
                  onChange={(e) => {
                    handleFormChange("subscriptionDuration", e.target.value);
                    if (fieldErrors.subscriptionDuration) {
                      setFieldErrors(prev => ({ ...prev, subscriptionDuration: "" }));
                    }
                  }}
                  fullWidth
                />
                {fieldErrors.subscriptionDuration && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                    {fieldErrors.subscriptionDuration}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Second Row - Set Coins and Set Amount */}
            <Box sx={{ display: "flex", gap: "20px" }}>
              <Box sx={{ flex: 1 }}>
                <CustomInputField
                  label="Set Coins"
                  placeholder="Enter Coins"
                  value={subscriptionForm.coins}
                  onChange={(e) => {
                    handleFormChange("coins", e.target.value);
                    if (fieldErrors.coins) {
                      setFieldErrors(prev => ({ ...prev, coins: "" }));
                    }
                  }}
                  fullWidth
                />
                {fieldErrors.coins && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                    {fieldErrors.coins}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomInputField
                  label="Set Amount"
                  placeholder="Set Amount"
                  value={subscriptionForm.amount}
                  onChange={(e) => {
                    handleFormChange("amount", e.target.value);
                    if (fieldErrors.amount) {
                      setFieldErrors(prev => ({ ...prev, amount: "" }));
                    }
                  }}
                  fullWidth
                />
                {fieldErrors.amount && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                    {fieldErrors.amount}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "40px",
              paddingBottom: "8px"
            }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!isFormChanged() || actionLoading}
                sx={{
                  borderColor: "#E1AD01",
                  color: "#E1AD01",
                  padding: "14px 40px",
                  borderRadius: "30px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  minWidth: "140px",
                  borderWidth: "2px",
                  "&.Mui-disabled": {
                    borderColor: "#E1AD01",
                    color: "#E1AD01",
                    opacity: 0.6,
                    pointerEvents: "none",
                  },
                  "&:hover": {
                    borderColor: "#E6B12A",
                    backgroundColor: "rgba(244, 196, 48, 0.08)",
                    borderWidth: "2px",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleCustomize}
                disabled={actionLoading}
                sx={{
                  backgroundColor: "#E1AD01",
                  color: "#000000",
                  padding: "14px 40px",
                  borderRadius: "30px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  minWidth: "140px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#E6B12A",
                    boxShadow: "0px 4px 12px rgba(244, 196, 48, 0.3)",
                  },
                }}
              >
                {actionLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#000000' }} />
                    Creating...
                  </Box>
                ) : (
                  'Customize'
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Subscription;